<template>
	<div class="ddd-toolbar">
		<div ref="datePickerRef" class="ddd-date-trigger-wrap">
			<DateRangePicker
				v-model="localRange"
				:published-range="dummyRange"
				:planning-range="dummyRange"
				:hide-sidebar="true"
				@update:model-value="handleDateRangeChange"
				@change="handleDateRangeChange"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { DateTime } from 'luxon';
import type { DateRange } from '@/rennovation/shared/types/date-range-picker';
import DateRangePicker from '@/rennovation/shared/components/DateRangePicker.vue';

// ─────────────────────────────────────────────────────────────────────────────
// Props / emits
// ─────────────────────────────────────────────────────────────────────────────
const props = defineProps<{
	startDate: string | null;
	endDate: string | null;
}>();

const emit = defineEmits<{
	(e: 'range-change', start: string, end: string): void;
	(e: 'range-clear'): void;
}>();

// ─────────────────────────────────────────────────────────────────────────────
// Local state — keeps DateRangePicker's v-model in sync with applied dates
// ─────────────────────────────────────────────────────────────────────────────
const dummyRange = computed<DateRange>(() => ({ startDate: null, endDate: null }));
const localRange = ref<DateRange>({
	startDate: props.startDate ? new Date(props.startDate) : null,
	endDate: props.endDate ? new Date(props.endDate) : null,
});

// Keep localRange in sync if parent changes applied dates externally
// (e.g. restored from localStorage on mount)
watch(
	() => [props.startDate, props.endDate] as const,
	([start, end]) => {
		localRange.value = {
			startDate: start ? new Date(start) : null,
			endDate: end ? new Date(end) : null,
		};
	}
);

function getDefaultRange(): { start: string; end: string } {
	return {
		start: DateTime.now().minus({ months: 6 }).toISODate()!,
		end: DateTime.now().plus({ months: 6 }).toISODate()!,
	};
}

// ─────────────────────────────────────────────────────────────────────────────
// Handlers
// ─────────────────────────────────────────────────────────────────────────────
function handleDateRangeChange(range: DateRange): void {
	if (!range.startDate || !range.endDate) {
		const { start, end } = getDefaultRange();

		localRange.value = {
			startDate: new Date(start + 'T12:00:00'),
			endDate: new Date(end + 'T12:00:00'),
		};

		emit('range-change', start, end);
		return;
	}
	const from = DateTime.fromJSDate(range.startDate as Date).toISODate()!;
	const to = DateTime.fromJSDate(range.endDate as Date).toISODate()!;
	emit('range-change', from, to);
}
</script>

<style scoped>
.ddd-toolbar {
	display: flex;
	align-items: center;
	padding: 6px 16px;
	border-bottom: 1px solid var(--ddd-border);
	flex-shrink: 0;
	background: var(--ddd-bg-toolbar);
	min-height: var(--ddd-toolbar-h);
}

.ddd-date-trigger-wrap {
	display: inline-flex;
	position: relative;
}
</style>
